import {
  Badge,
  Card,
  EmptyState,
  FilterBar,
  FilterChips,
  FormField,
  MasterDetail,
  PageHeader,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  cn,
  useMasterDetailWide,
} from '@hemmafixa/ui';
import {
  formatPostalCode,
  WANTED_STARTS,
  type ProviderJobListItem,
  type ProviderJobListQuery,
  type WantedStart,
} from '@hemmafixa/shared';
import { Bookmark, Briefcase, MapPin, MousePointerClick, SearchX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { fill, useI18n } from '@/i18n/context';
import { useCategoryLabel, useCategoryOptions } from '../shared/catalogue';
import { DetailPanel, FullPageLink } from '../shared/master-detail-parts';
import { QueryView } from '../shared/query-view';
import { useSelectedParam } from '../shared/use-selected-param';
import { useStatuses } from '../shared/statuses';
import { useOpenJobs } from './api';
import { ProviderJobDetail } from './provider-job-page';
import { SaveJobButton } from './save-job-button';
import { SubscribersOnly, VerifiedOnly } from './verified-only';

const PAGE_SIZE = 10;

type Tab = 'all' | 'saved';
type ProposalFilter = ProviderJobListQuery['proposal'];

/** Matching open jobs to quote on (P4-06), with filters, saved jobs and a details panel (P4-03). */
export function FindJobsPage() {
  const { t } = useI18n();
  return (
    <div className="grid gap-4">
      <PageHeader title={t.provider.findJobs.title} description={t.provider.findJobs.description} />
      <VerifiedOnly>
        <SubscribersOnly>
          <JobList />
        </SubscribersOnly>
      </VerifiedOnly>
    </div>
  );
}

function JobList() {
  const { t } = useI18n();
  const categories = useCategoryOptions().filter((option) => option.isMain);
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(params.get('saved') === 'true' ? 'saved' : 'all');
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [searchQuery, setSearchQuery] = useState(search);
  const [category, setCategory] = useState('all');
  const [wantedStart, setWantedStart] = useState<WantedStart | 'all'>('all');
  const [proposal, setProposal] = useState<ProposalFilter>(() => {
    const value = params.get('proposal');
    return value === 'none' || value === 'sent' ? value : 'all';
  });
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useSelectedParam();
  const wide = useMasterDetailWide();
  const f = t.provider.findJobs;

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const query = useOpenJobs({
    q: searchQuery || undefined,
    category: category === 'all' ? undefined : category,
    wantedStart: wantedStart === 'all' ? undefined : wantedStart,
    proposal: proposal === 'all' ? undefined : proposal,
    saved: tab === 'saved' || undefined,
    page,
    pageSize: PAGE_SIZE,
  });
  const filtering = search !== '' || category !== 'all' || wantedStart !== 'all' || proposal !== 'all';
  const items = query.data?.items ?? [];
  // From `lg` the panel follows the list: a job that left it (unsaved, filtered away) gives way to the first one.
  // On phones nothing opens until a job is tapped; a job from a shared link opens even when not on this page.
  const selected = wide
    ? (items.find((job) => job.id === selectedId)?.id ?? items[0]?.id ?? null)
    : selectedId;

  /** Every filter change starts again on page 1 */
  const change =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };

  return (
    <>
      <FilterBar
        search={search}
        onSearchChange={change(setSearch)}
        searchPlaceholder={f.searchPlaceholder}
        summary={
          <span className="flex items-center gap-3">
            {query.data &&
              fill(t.common.showingCount, { shown: query.data.items.length, total: query.data.total })}
            {/* Beside the chips, so the details start level with the list */}
            {selected && <FullPageLink to={`/provider/jobs/${selected}`} className="hidden lg:inline-flex" />}
          </span>
        }
        hasActiveFilters={filtering}
        onClear={() => {
          setSearch('');
          setCategory('all');
          setWantedStart('all');
          setProposal('all');
          setPage(1);
        }}
        clearLabel={t.common.clearFilters}
        chips={
          <FilterChips
            label={f.show}
            value={tab}
            onChange={change(setTab)}
            options={[
              { value: 'all', label: f.allJobs },
              { value: 'saved', label: f.savedJobs },
            ]}
          />
        }
      >
        {/* FormField cannot reach the trigger inside Select, so each trigger gets its own name */}
        <FormField label={t.job.category}>
          <Select value={category} onValueChange={change(setCategory)}>
            <SelectTrigger aria-label={t.job.category}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{f.allCategories}</SelectItem>
              {categories.map((option) => (
                <SelectItem key={option.slug} value={option.slug}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label={t.job.wantedStart}>
          <Select
            value={wantedStart}
            onValueChange={change((value: string) => setWantedStart(value as WantedStart | 'all'))}
          >
            <SelectTrigger aria-label={t.job.wantedStart}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{f.anyStart}</SelectItem>
              {WANTED_STARTS.map((value) => (
                <SelectItem key={value} value={value}>
                  {t.wantedStart[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label={f.yourProposal}>
          <Select
            value={proposal}
            onValueChange={change((value: string) => setProposal(value as ProposalFilter))}
          >
            <SelectTrigger aria-label={f.yourProposal}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.common.all}</SelectItem>
              <SelectItem value="none">{f.notQuoted}</SelectItem>
              <SelectItem value="sent">{f.quoted}</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
      </FilterBar>
      <QueryView query={query} loading="cards">
        {(result) =>
          result.items.length === 0 ? (
            filtering ? (
              <EmptyState
                icon={SearchX}
                title={t.common.noMatchesTitle}
                description={t.common.noMatchesText}
              />
            ) : tab === 'saved' ? (
              <EmptyState icon={Bookmark} title={f.savedEmpty} description={f.savedEmptyText} />
            ) : (
              <EmptyState icon={Briefcase} title={f.empty} description={f.emptyText} />
            )
          ) : (
            <MasterDetail
              open={selected !== null}
              onClose={() => setSelectedId(null)}
              labels={{ details: f.details }}
              placeholder={<EmptyState icon={MousePointerClick} title={f.pickJob} />}
              detail={selected ? <JobPanel id={selected} /> : null}
              list={
                <>
                  <ul className="grid gap-3">
                    {result.items.map((job) => (
                      <li key={job.id}>
                        <JobCard
                          job={job}
                          selected={job.id === selected}
                          onSelect={() => setSelectedId(job.id)}
                        />
                      </li>
                    ))}
                  </ul>
                  <Pagination
                    page={result.page}
                    pageSize={result.pageSize}
                    total={result.total}
                    onPageChange={setPage}
                    labels={{
                      previous: t.common.previous,
                      next: t.common.next,
                      showing: (from, to, total) => fill(t.common.showingRange, { from, to, total }),
                    }}
                  />
                </>
              }
            />
          )
        }
      </QueryView>
    </>
  );
}

interface JobCardProps {
  job: ProviderJobListItem;
  selected: boolean;
  /** Shows the job in the details panel; Ctrl/Cmd-click still opens the job page */
  onSelect: () => void;
}

function JobCard({ job, selected, onSelect }: JobCardProps) {
  const { t, relative } = useI18n();
  const statuses = useStatuses();
  const categoryLabel = useCategoryLabel();

  return (
    <Card
      className={cn(
        'relative grid gap-2 p-4 transition-colors hover:border-primary/60',
        selected && 'border-primary ring-1 ring-primary',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="font-semibold">
          {/* The link covers the whole card; the save button sits above it */}
          <Link
            to={`/provider/jobs/${job.id}`}
            aria-current={selected || undefined}
            className="after:absolute after:inset-0 after:rounded-[inherit] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring"
            onClick={(event) => {
              if (event.metaKey || event.ctrlKey || event.shiftKey) return;
              event.preventDefault();
              onSelect();
            }}
          >
            {job.title}
          </Link>
        </h2>
        <div className="relative z-10 -mr-2 -mt-2">
          <SaveJobButton jobId={job.id} saved={job.saved} compact />
        </div>
      </div>
      <div>
        <Badge tone="brand">{categoryLabel(job.categorySlug)}</Badge>
      </div>
      <p className="line-clamp-2 text-sm text-muted-foreground">{job.description}</p>
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <MapPin className="size-3.5" aria-hidden />
          {formatPostalCode(job.postalCode)}
        </span>
        <span>{t.wantedStart[job.wantedStart]}</span>
        {job.publishedAt && <span>{relative(job.publishedAt)}</span>}
        <span>{fill(t.client.proposalCount, { count: job.proposalCount })}</span>
      </p>
      {job.myProposalStatus && (
        <StatusBadge
          status={job.myProposalStatus}
          statuses={statuses.proposal}
          className="justify-self-start"
        />
      )}
    </Card>
  );
}

/** The selected job beside the list, in full: read it, save it and send a proposal without leaving the list. */
function JobPanel({ id }: { id: string }) {
  return (
    <DetailPanel fullPage={`/provider/jobs/${id}`} linkBesideTabs>
      <ProviderJobDetail id={id} />
    </DetailPanel>
  );
}
